// Hybrid Logical Clock encoded as a fixed-width string so that plain
// lexicographic comparison (JS `<`, Postgres COLLATE "C") gives causal order:
//   <wall ms, 15 digits>-<counter, 5 base36 chars>-<node id>

const MS_WIDTH = 15;
const COUNTER_WIDTH = 5;
const MAX_COUNTER = 36 ** COUNTER_WIDTH - 1;

export function encodeHlc(ms, counter, node) {
  return `${String(ms).padStart(MS_WIDTH, '0')}-${counter
    .toString(36)
    .padStart(COUNTER_WIDTH, '0')}-${node}`;
}

export function parseHlc(hlc) {
  const [ms, counter, node] = hlc.split('-');
  return { ms: Number(ms), counter: parseInt(counter, 36), node };
}

export function compareHlc(a, b) {
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return a < b ? -1 : 1;
}

export function maxHlc(values) {
  let max = null;
  for (const v of values) if (v != null && (max == null || v > max)) max = v;
  return max;
}

/** Advance the clock for a local event. `state` is { ms, counter }. */
export function tick(state, node, now = Date.now()) {
  let ms = Math.max(state.ms, now);
  let counter = ms === state.ms ? state.counter + 1 : 0;
  if (counter > MAX_COUNTER) {
    ms += 1;
    counter = 0;
  }
  return { state: { ms, counter }, hlc: encodeHlc(ms, counter, node) };
}

/** Merge a remote timestamp into the local clock so later local events sort after it. */
export function receive(state, remoteHlc, now = Date.now()) {
  if (!remoteHlc) return state;
  const r = parseHlc(remoteHlc);
  const ms = Math.max(state.ms, r.ms, now);
  let counter;
  if (ms === state.ms && ms === r.ms) counter = Math.max(state.counter, r.counter) + 1;
  else if (ms === state.ms) counter = state.counter + 1;
  else if (ms === r.ms) counter = r.counter + 1;
  else counter = 0;
  return { ms, counter: Math.min(counter, MAX_COUNTER) };
}
